import React from 'react';

import { Box, Typography } from '@mui/material';

import PartidoAlineacionesProbables from '@/components/alineaciones/partido/PartidoAlineacionesProbables';
import { IsrRevalidateTime } from '@/constants/cache-state-constants';
import URL_FACTORY from '@/helpers/url-helper';
import axiosApiInstance from '@/api/shared/axios-instance';
import { Pages, Rutas } from '@/constants/rutas';
import { mapToAlineacionesProbables } from '@/components/alineaciones/mappers/MapToAlineacionesProbables';
import Seo from '@/components/shared/custom/seo-component';
import Custom404Client from '@/components/site/Custom404Client';
import { isValidFixtureId } from '@/helpers/fixtures-helper';

/**
 * Genera los datos SEO para la página de alineaciones
 * @param {Object} lineupsData - Datos de las alineaciones
 * @returns {Object} Datos SEO
 */
export const getSeoData = (lineupsData) => {
    const isPredicFixture = new Date(lineupsData?.fixture?.date) > new Date();
    const fixtureId = lineupsData?.fixture?.id;
    const homeName = lineupsData?.home?.name;
    const awayName = lineupsData?.away?.name;

    // Si el partido es futuro, siempre mostrar "probables" (no hay confirmadas aún)
    if (isPredicFixture) {
        return {
            pageId: Pages.PARTIDO,
            title: `Alineaciones probables de ${homeName} vs ${awayName}.`,
            description: `Alineaciones probables de ${homeName} vs ${awayName}. Posibles onces, suplentes, bajas y sancionados, apercibidos, recomendaciones de Don Quijote de la Cancha en clave fantasy marca, biwenger, mister fantasy y la liga fantasy.`,
            image: `${process.env.NEXT_PUBLIC_IMAGES}/alineaciones-probables/${fixtureId}.webp`,
            url: `${process.env.NEXT_PUBLIC_URL_FRONT}${Rutas.PARTIDO}/${fixtureId}/alineaciones-probables`,
            h1: `${homeName} vs ${awayName}: Alineaciones probables.`,
        };
    }

    return {
        pageId: Pages.PARTIDO,
        title: `Alineaciones confirmadas de ${homeName} vs ${awayName}.`,
        description: `Alineaciones confirmadas de ${homeName} vs ${awayName}. estadísticas de sofascore, estadísticas fantasy, mejores jugadores en clave fantasy la liga, biwenger, mister y fantasy marca`,
        image: `${process.env.NEXT_PUBLIC_IMAGES}/alineaciones-confirmadas/${fixtureId}.webp`,
        url: `${process.env.NEXT_PUBLIC_URL_FRONT}${Rutas.PARTIDO}/${fixtureId}/alineaciones-confirmadas`,
        h1: `${homeName} vs ${awayName}: Alineaciones confirmadas.`,
    };
};

/**
 * Componente de página de alineaciones reutilizable
 * @param {Object} props
 * @param {Object} props.lineupsResponse - Respuesta de la API con los datos de alineaciones
 * @param {boolean} props.invalidSlug - Indica si el slug es inválido
 * @param {string} props.type - Tipo de alineación: 'probables' o 'confirmadas'
 */
export const PartidoAlineacionesPage = ({ lineupsResponse, invalidSlug }) => {
    if (invalidSlug) {
        console.log('invalidSlug', invalidSlug);
        return <Custom404Client />;
    }

    const lineupsData = mapToAlineacionesProbables(lineupsResponse);
    const { home, away } = lineupsData;

    if (!lineupsData || !home || !away) {
        return <Custom404Client />;
    }

    const seoData = getSeoData(lineupsData);

    return (
        <Box className="container">
            <Seo {...seoData} />

            <Typography component="h1" mb={1}>
                {seoData.h1}
            </Typography>

            <PartidoAlineacionesProbables data={lineupsData} />
        </Box>
    );
};

/**
 * Función getStaticPaths compartida
 */
export const getStaticPaths = () => {
    return {
        paths: [],
        fallback: 'blocking',
    };
};

/**
 * Función getStaticProps compartida
 * @param {Object} params
 * @param {Object} params.params - Parámetros de la ruta
 * @param {string} params.params.slug - ID del fixture
 */
export const getStaticProps = async ({ params }) => {
    const { slug } = params;

    try {
        // Validación temprana del slug para evitar llamadas al backend y doble request
        if (!isValidFixtureId(slug)) {
            return {
                props: {
                    fixtureId: null,
                    lineupsResponse: null,
                    invalidSlug: true,
                },
                // Revalidación corta por si el usuario arregla la URL y navega
                revalidate: 5,
            };
        }

        const url = URL_FACTORY.ObtenerAlineacionesProbables(slug);
        const response = await axiosApiInstance.get(url);
        const lineupsResponse = response.data;

        return {
            props: {
                fixtureId: slug,
                lineupsResponse: lineupsResponse,
                invalidSlug: false,
            },
            revalidate:
                lineupsResponse.revalidate > 0
                    ? lineupsResponse.revalidate
                    : IsrRevalidateTime.ALINEACIONES_PARTIDO_PAGE,
        };
    } catch (_error) {
        console.error('Error fetching data:', _error);
        return {
            props: {
                fixtureId: slug,
                lineupsResponse: null,
                invalidSlug: false,
            },
            revalidate: 5,
        };
    }
};
